/** Bounded recovery for a browser voice attachment. Never replays audio or backend requests. */
export class LiveConnectionRecovery {
  readonly #connect: () => void;
  readonly #exhausted: () => void;
  #attempts = 0;
  #stopped = false;
  #retry: ReturnType<typeof setTimeout> | undefined;
  #deadline: ReturnType<typeof setTimeout> | undefined;
  #stable: ReturnType<typeof setTimeout> | undefined;

  constructor(connect: () => void, exhausted: () => void) {
    this.#connect = connect;
    this.#exhausted = exhausted;
  }

  retry(): void {
    if (this.#stopped || this.#retry) return;
    clearTimeout(this.#stable);
    this.#deadline ??= setTimeout(() => this.#fail(), 60_000);
    if (this.#attempts >= 6) {
      this.#fail();
      return;
    }
    const delay = Math.min(250 * 2 ** this.#attempts++, 4_000);
    this.#retry = setTimeout(() => {
      this.#retry = undefined;
      this.#connect();
    }, delay);
  }

  ready(): void {
    clearTimeout(this.#retry);
    clearTimeout(this.#deadline);
    clearTimeout(this.#stable);
    this.#retry = undefined;
    this.#deadline = undefined;
    // A flapping connection must not reset its retry budget on every activation.
    this.#stable = setTimeout(() => {
      this.#attempts = 0;
    }, 10_000);
  }

  stop(): void {
    this.#stopped = true;
    clearTimeout(this.#retry);
    clearTimeout(this.#deadline);
    clearTimeout(this.#stable);
  }

  #fail(): void {
    this.stop();
    this.#exhausted();
  }
}

/** Unknown transport loss is retryable; protocol, authentication and policy refusals are final. */
export function isRetryableVoiceClose(code: number | undefined): boolean {
  return code === undefined || [1000, 1001, 1005, 1006, 1011, 1012, 1013].includes(code);
}
